import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth, db } from '@/lib/db'
import { setAuthToken } from '@/lib/api'
import { Loader2 } from 'lucide-react'
import type { Profile } from '@/types'

interface AuthGuardProps {
  children: ReactNode
}

/**
 * 路由守卫组件
 * 
 * 守卫逻辑：
 * 1. 未登录 -> 跳转到 /login
 * 2. 已登录但无 org_id -> 跳转到 /join
 * 3. 已登录且有 org_id -> 正常访问
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // 查询当前用户档案
  const { data: profileData, isLoading: isProfileLoading } = db.useQuery({
    profiles: {
      $: {
        where: { id: auth.user?.id },
      },
    },
  })

  const currentProfile = profileData?.profiles?.[0] as Profile | undefined

  // 缓存认证 token，供 fetchWithAuth 使用
  useEffect(() => {
    const token = auth.user?.refresh_token ?? null
    setAuthToken(token)
    if (token) {
      console.log('[AuthGuard] Token 已缓存，长度:', token.length)
    }
  }, [auth.user?.refresh_token])

  useEffect(() => {
    // 1. 未登录 -> 跳转到登录页
    if (!auth.isLoading && !auth.user) {
      setAuthToken(null) // 清除缓存
      navigate('/login', { replace: true, state: { from: location.pathname } })
      return
    }

    // 2. 已登录但无企业 -> 跳转到邀请码绑定页
    if (auth.user && !isProfileLoading && !currentProfile?.org_id) {
      navigate('/join', { replace: true })
      return
    }
  }, [auth.isLoading, auth.user, isProfileLoading, currentProfile, navigate, location.pathname])

  // 加载中
  if (auth.isLoading || isProfileLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">加载中...</p>
        </div>
      </div>
    )
  }

  // 未登录或无企业，守卫会自动跳转
  if (!auth.user || !currentProfile?.org_id) {
    return null
  }

  // 验证通过，渲染子组件
  return <>{children}</>
}


